import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  dashboardConfigApi,
  type DashboardConfigDetail,
  type DashboardConfigListItem,
  type MetricOption,
} from '@/lib/api/dashboard-config.api';

import type { DashboardConfig, TreeNodeMetric } from '@repo/shared-types';

const QUERY_KEY = ['dashboard-configs'];

export function useMetricOptions() {
  return useQuery<MetricOption[]>({
    queryKey: ['metric-options'],
    queryFn: () => dashboardConfigApi.getMetricOptions(),
    // No `staleTime: Infinity` here — metric_definitions rows can be added
    // out-of-band (admin UI, SQL seeds, other browser tabs). Refetching on
    // mount keeps the dropdown in sync without forcing a hard refresh.
    staleTime: 30_000,
  });
}

export function useDashboardConfigList() {
  return useQuery<DashboardConfigListItem[]>({
    queryKey: QUERY_KEY,
    queryFn: () => dashboardConfigApi.list(),
  });
}

export function useDashboardConfigDetail(id: string | undefined) {
  return useQuery<DashboardConfigDetail>({
    queryKey: [...QUERY_KEY, id],
    queryFn: () => dashboardConfigApi.getById(id!),
    enabled: !!id,
  });
}

export function useCreateDashboardConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      config: DashboardConfig;
      isDefault?: boolean;
      type?: string;
    }) => dashboardConfigApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
    },
  });
}

export function useUpdateDashboardConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { name?: string; config?: DashboardConfig; type?: string };
    }) => dashboardConfigApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
    },
  });
}

export function useSetDefaultDashboardConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dashboardConfigApi.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
    },
  });
}

export function useDeleteDashboardConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dashboardConfigApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useDashboardConfigData(id: string | undefined) {
  return useQuery<Record<string, TreeNodeMetric>>({
    queryKey: [...QUERY_KEY, id, 'data'],
    queryFn: () => dashboardConfigApi.getData(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}
